import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminApi } from '../../../services/api';

/**
 * Note Generation Sandbox Redux Slice
 *
 * Purpose: Mix & match testing tool for note generation
 * - Load existing cases, transcripts, providers, templates
 * - Override any component to test different combinations
 * - Generate notes with custom configurations
 * - Compare results side-by-side
 */

/**
 * Async Thunks - Using existing admin APIs
 */

// Load list of cases
export const fetchCases = createAsyncThunk(
  'noteGenerationSandbox/fetchCases',
  async ({ limit = 50, status, healthSystemId } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (limit) params.limit = limit;
      if (status) params.status = status;
      if (healthSystemId) params.health_system_id = healthSystemId;

      const response = await adminApi.getCases(params);
      // Handle response format: { data: { success: true, data: [...] } }
      if (response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      // Fallback: check if response.data is already an array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Load complete case data (transcript, provider, patient, etc.)
export const loadCase = createAsyncThunk(
  'noteGenerationSandbox/loadCase',
  async (caseId, { rejectWithValue }) => {
    try {
      // Fetch case details
      const caseResponse = await adminApi.getCaseDetails(caseId);

      // Fetch audio sessions to get transcript
      const sessionsResponse = await adminApi.getCaseAudioSessions(caseId);

      // Get latest session with transcript
      let transcript = null;
      let utterances = [];
      if (sessionsResponse.data.audio_sessions?.length > 0) {
        const latestSession = sessionsResponse.data.audio_sessions[0];
        const sessionDetailResponse = await adminApi.get(`/provider/audio/session/${latestSession.session_id}/status/`);
        transcript = sessionDetailResponse.data.session_transcript?.transcript_text;
        utterances = sessionDetailResponse.data.session_transcript?.utterances || [];
      }

      // Fetch existing notes for comparison
      const notesResponse = await adminApi.get(`/healthcare/cases/${caseId}/notes/`);

      // Extract case from response (backend returns {success: true, case: {...}})
      const caseData = caseResponse.data.case || caseResponse.data;

      return {
        case: caseData,
        transcript,
        utterances,
        existingNotes: notesResponse.data.visit_notes || []
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch all providers (for override dropdown)
export const fetchProviders = createAsyncThunk(
  'noteGenerationSandbox/fetchProviders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminApi.getProviders();
      // Handle response format: { success: true, providers: [...] }
      if (response.data.success && Array.isArray(response.data.providers)) {
        return response.data.providers;
      }
      // Fallback: check if response.data.data exists (alternate format)
      if (response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      // Fallback: check if response.data is already an array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch all templates (for override dropdown)
export const fetchTemplates = createAsyncThunk(
  'noteGenerationSandbox/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminApi.fetchAdminNoteTemplates();
      // Handle response format: bare array directly in response.data
      if (Array.isArray(response.data)) {
        return response.data;
      }
      // Fallback: check if wrapped in success/data structure
      if (response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Generate note with custom configuration
export const generateNote = createAsyncThunk(
  'noteGenerationSandbox/generateNote',
  async ({
    transcript,
    providerId,
    caseId,
    userTemplateContent,
    llmPromptTemplateId,
    customInstructions,
    customLLMPrompt,
    providerPreferencesOverride,
    priorNotesData,
    speakerLabels,
    generationOptions,
    saveRun = true,
    runName,
    runDescription,
    runTags
  }, { rejectWithValue }) => {
    try {
      // Build request body
      const requestBody = {
        transcript,
        provider_id: providerId,
        generation_options: generationOptions,
        save_run: saveRun
      };

      // Add optional parameters if provided
      if (caseId) {
        requestBody.case_id = caseId;
      }

      // Always send user_template_content (empty string if none)
      requestBody.user_template_content = userTemplateContent || '';

      if (llmPromptTemplateId) {
        requestBody.llm_prompt_template_id = llmPromptTemplateId;
      }

      // Always send custom_instructions so backend has it (empty string if none)
      requestBody.custom_instructions = customInstructions || '';

      // Add run metadata if saving
      if (saveRun) {
        if (runName) {
          requestBody.run_name = runName;
        }
        if (runDescription) {
          requestBody.run_description = runDescription;
        }
        if (runTags && runTags.length > 0) {
          requestBody.run_tags = runTags;
        }
      }

      // Add custom LLM prompt if provided and customized
      if (customLLMPrompt && customLLMPrompt.isCustomized) {
        if (customLLMPrompt.system_message) {
          requestBody.custom_system_message = customLLMPrompt.system_message;
        }
        if (customLLMPrompt.json_schema) {
          // Try to parse as JSON if it's a string
          try {
            requestBody.custom_json_schema = typeof customLLMPrompt.json_schema === 'string'
              ? JSON.parse(customLLMPrompt.json_schema)
              : customLLMPrompt.json_schema;
          } catch (e) {
            // If parsing fails, send as string and let backend handle it
            requestBody.custom_json_schema = customLLMPrompt.json_schema;
          }
        }
      }

      // Filter out null values from provider preferences override
      if (providerPreferencesOverride) {
        const filteredPreferences = Object.entries(providerPreferencesOverride)
          .filter(([, value]) => value !== null)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

        if (Object.keys(filteredPreferences).length > 0) {
          requestBody.provider_preferences_override = filteredPreferences;
        }
      }

      if (priorNotesData && priorNotesData.length > 0) {
        requestBody.prior_notes_data = priorNotesData;
      }

      if (speakerLabels && speakerLabels.length > 0) {
        requestBody.speaker_labels = speakerLabels;
      }

      const response = await adminApi.post(
        '/pipeline-debug/note/generate/',
        requestBody
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Convert note to multiple formats
// Supports two modes:
// 1. Direct conversion: Pass note_sections JSON
// 2. Database fetch: Pass note_id to fetch existing note
export const convertNoteFormats = createAsyncThunk(
  'noteGenerationSandbox/convertNoteFormats',
  async ({ noteSections, noteId, contentType = 'all' }, { rejectWithValue }) => {
    try {
      const requestBody = {
        content_type: contentType
      };

      // Add either note_sections or note_id
      if (noteId) {
        requestBody.note_id = noteId;
      } else if (noteSections) {
        requestBody.note_sections = noteSections;
      } else {
        return rejectWithValue({ error: 'Must provide either noteSections or noteId' });
      }

      const response = await adminApi.post(
        '/note/convert/',
        requestBody
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch available note_generation_llm models
export const fetchAvailableModels = createAsyncThunk(
  'noteGenerationSandbox/fetchAvailableModels',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminApi.get('/pipeline-debug/note-generation-llm/list-models/');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch available LLM prompt templates
export const fetchLLMPromptTemplates = createAsyncThunk(
  'noteGenerationSandbox/fetchLLMPromptTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminApi.get('/admin/note/system/prompts/');
      // Response format: { success: true, prompts: [...] }
      if (response.data.success && Array.isArray(response.data.prompts)) {
        return response.data.prompts;
      }
      // Fallback: check if response.data is already an array
      if (Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Redux Slice
 */
const noteGenerationSandboxSlice = createSlice({
  name: 'noteGenerationSandbox',
  initialState: {
    // Case browser
    availableCases: [],
    casesStatus: 'idle', // 'idle' | 'loading' | 'success' | 'error'
    casesError: null,

    // Loaded case data
    selectedCase: null,
    transcript: '',
    utterances: [],
    existingNotes: [],
    loadCaseStatus: 'idle',
    loadCaseError: null,

    // Override options
    availableProviders: [],
    availableTemplates: [],
    availableModels: [],
    availableLLMPromptTemplates: [],
    selectedProviderId: null, // null = use case's provider
    selectedTemplateId: null, // null = use provider's default
    customNoteTemplateContent: '', // when non-empty, overrides selected note template for this run
    selectedLLMPromptTemplateId: null, // null = use system default
    providersStatus: 'idle',
    templatesStatus: 'idle',
    modelsStatus: 'idle',
    llmPromptTemplatesStatus: 'idle',
    modelsError: null,
    llmPromptTemplatesError: null,

    // Editable transcript (can override loaded transcript)
    editedTranscript: '',
    transcriptEdited: false,

    // Generation config
    customInstructions: '',
    customLLMPrompt: {
      system_message: '',
      json_schema: '',
      isCustomized: false // Flag to track if user has edited the template
    },
    providerPreferencesOverride: {
      note_brevity: null, // null = use provider's default | 'brief' | 'standard' | 'detailed'
      writing_style: null, // null = use provider's default | 'professional' | 'conversational' | 'clinical'
      include_differential: null, // null = use provider's default | true | false
      emphasize_plan: null, // null = use provider's default | true | false
      patient_education_focus: null // null = use provider's default | true | false
    },
    priorNotesConfig: {
      enabled: false,
      sections: [] // Array of {section_name: string, content: string, handling: 'as-is' | 'context'}
    },
    generationOptions: {
      model: 'gpt-5.1',
      temperature: 0.0,
      max_tokens: 15000,
      reasoning_effort: 'none' // For GPT-5 and above: none | low | medium | high | xhigh
    },

    // Generated results
    generatedNote: null, // note_sections from LLM
    generatedFormats: {
      html: '',
      plain_text: '',
      rtf: '',
      formatted_text: ''
    },
    generateStatus: 'idle',
    generateError: null,
    convertStatus: 'idle', // 'idle' | 'loading' | 'success' | 'error'
    convertError: null,

    // Final prompts sent to LLM (for debugging)
    finalUserMessage: '',
    finalSystemMessage: '',

    // Comparison
    compareWithNoteId: null, // ID of existing note to compare with
    comparisonView: 'side-by-side', // 'side-by-side' | 'diff' | 'split'

    // UI state
    showTranscriptEditor: false,
    showAdvancedOptions: false,
    activeTab: 'json', // 'json' | 'html' | 'plain' | 'rtf'
  },

  reducers: {
    // Case selection
    selectCase: (state, action) => {
      state.selectedCase = action.payload;
    },
    clearCase: (state) => {
      state.selectedCase = null;
      state.transcript = '';
      state.utterances = [];
      state.existingNotes = [];
      state.editedTranscript = '';
      state.transcriptEdited = false;
      state.generatedNote = null;
    },

    // Override controls
    setSelectedProvider: (state, action) => {
      state.selectedProviderId = action.payload;
    },
    setSelectedTemplate: (state, action) => {
      state.selectedTemplateId = action.payload;
      state.customNoteTemplateContent = '';
    },
    updateCustomNoteTemplateContent: (state, action) => {
      state.customNoteTemplateContent = action.payload ?? '';
    },
    setSelectedLLMPromptTemplate: (state, action) => {
      state.selectedLLMPromptTemplateId = action.payload;

      // Load the template content into customLLMPrompt if a template is selected
      if (action.payload) {
        const template = state.availableLLMPromptTemplates.find(t => t.id === action.payload);
        if (template) {
          state.customLLMPrompt = {
            system_message: template.system_message || '',
            json_schema: typeof template.json_schema === 'string'
              ? template.json_schema
              : JSON.stringify(template.json_schema, null, 2),
            isCustomized: false
          };
        }
      } else {
        // Clear custom prompt when no template is selected
        state.customLLMPrompt = {
          system_message: '',
          json_schema: '',
          isCustomized: false
        };
      }
    },
    updateCustomLLMPrompt: (state, action) => {
      state.customLLMPrompt = {
        ...state.customLLMPrompt,
        ...action.payload,
        isCustomized: true // Mark as customized when user edits
      };
    },
    resetCustomLLMPrompt: (state) => {
      // Reset to the selected template's original content
      if (state.selectedLLMPromptTemplateId) {
        const template = state.availableLLMPromptTemplates.find(t => t.id === state.selectedLLMPromptTemplateId);
        if (template) {
          state.customLLMPrompt = {
            system_message: template.system_message || '',
            json_schema: typeof template.json_schema === 'string'
              ? template.json_schema
              : JSON.stringify(template.json_schema, null, 2),
            isCustomized: false
          };
        }
      } else {
        state.customLLMPrompt = {
          system_message: '',
          json_schema: '',
          isCustomized: false
        };
      }
    },
    updateProviderPreferences: (state, action) => {
      state.providerPreferencesOverride = { ...state.providerPreferencesOverride, ...action.payload };
    },
    resetProviderPreferences: (state) => {
      state.providerPreferencesOverride = {
        note_brevity: null,
        writing_style: null,
        include_differential: null,
        emphasize_plan: null,
        patient_education_focus: null
      };
    },
    updatePriorNotesConfig: (state, action) => {
      state.priorNotesConfig = { ...state.priorNotesConfig, ...action.payload };
    },
    addPriorNoteSection: (state, action) => {
      state.priorNotesConfig.sections.push(action.payload);
    },
    removePriorNoteSection: (state, action) => {
      state.priorNotesConfig.sections = state.priorNotesConfig.sections.filter((_, index) => index !== action.payload);
    },
    clearPriorNotes: (state) => {
      state.priorNotesConfig = {
        enabled: false,
        sections: []
      };
    },

    // Transcript editing
    setEditedTranscript: (state, action) => {
      state.editedTranscript = action.payload;
      state.transcriptEdited = true;
    },
    resetTranscript: (state) => {
      state.editedTranscript = state.transcript;
      state.transcriptEdited = false;
    },
    toggleTranscriptEditor: (state) => {
      state.showTranscriptEditor = !state.showTranscriptEditor;
    },

    // Generation config
    setCustomInstructions: (state, action) => {
      state.customInstructions = action.payload;
    },
    updateGenerationOptions: (state, action) => {
      state.generationOptions = { ...state.generationOptions, ...action.payload };
    },
    toggleAdvancedOptions: (state) => {
      state.showAdvancedOptions = !state.showAdvancedOptions;
    },

    // Comparison
    setCompareWithNote: (state, action) => {
      state.compareWithNoteId = action.payload;
    },
    setComparisonView: (state, action) => {
      state.comparisonView = action.payload;
    },

    // UI
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    // Reset everything
    resetSandbox: (state) => {
      return {
        ...state,
        selectedCase: null,
        transcript: '',
        utterances: [],
        existingNotes: [],
        editedTranscript: '',
        transcriptEdited: false,
        selectedProviderId: null,
        selectedTemplateId: null,
        customNoteTemplateContent: '',
        selectedLLMPromptTemplateId: null,
        customInstructions: '',
        customLLMPrompt: {
          system_message: '',
          json_schema: '',
          isCustomized: false
        },
        providerPreferencesOverride: {
          note_brevity: null,
          writing_style: null,
          include_differential: null,
          emphasize_plan: null,
          patient_education_focus: null
        },
        priorNotesConfig: {
          enabled: false,
          sections: []
        },
        generatedNote: null,
        generatedFormats: {
          html: '',
          plain_text: '',
          rtf: '',
          formatted_text: ''
        },
        compareWithNoteId: null,
        generationOptions: {
          model: 'gpt-5.1',
          temperature: 0.0,
          max_tokens: 15000,
          reasoning_effort: 'none'
        },
        finalUserMessage: '',
        finalSystemMessage: ''
      };
    }
  },

  extraReducers: (builder) => {
    // Fetch cases
    builder
      .addCase(fetchCases.pending, (state) => {
        state.casesStatus = 'loading';
        state.casesError = null;
      })
      .addCase(fetchCases.fulfilled, (state, action) => {
        state.casesStatus = 'success';
        state.availableCases = action.payload;
      })
      .addCase(fetchCases.rejected, (state, action) => {
        state.casesStatus = 'error';
        state.casesError = action.payload;
      });

    // Load case
    builder
      .addCase(loadCase.pending, (state) => {
        state.loadCaseStatus = 'loading';
        state.loadCaseError = null;
      })
      .addCase(loadCase.fulfilled, (state, action) => {
        state.loadCaseStatus = 'success';
        state.selectedCase = action.payload.case;
        state.transcript = action.payload.transcript || '';
        state.editedTranscript = action.payload.transcript || '';
        state.utterances = action.payload.utterances || [];
        state.existingNotes = action.payload.existingNotes || [];
        state.transcriptEdited = false;

        // Auto-select the case's provider and template (can be overridden)
        // Provider ID is nested in provider_info_fk
        state.selectedProviderId = action.payload.case.provider_info_fk?.id || null;
        state.selectedTemplateId = null; // Will use provider's default
      })
      .addCase(loadCase.rejected, (state, action) => {
        state.loadCaseStatus = 'error';
        state.loadCaseError = action.payload;
      });

    // Fetch providers
    builder
      .addCase(fetchProviders.pending, (state) => {
        state.providersStatus = 'loading';
      })
      .addCase(fetchProviders.fulfilled, (state, action) => {
        state.providersStatus = 'success';
        state.availableProviders = action.payload;
      })
      .addCase(fetchProviders.rejected, (state) => {
        state.providersStatus = 'error';
      });

    // Fetch templates
    builder
      .addCase(fetchTemplates.pending, (state) => {
        state.templatesStatus = 'loading';
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templatesStatus = 'success';
        state.availableTemplates = action.payload;
      })
      .addCase(fetchTemplates.rejected, (state) => {
        state.templatesStatus = 'error';
      });

    // Generate note
    builder
      .addCase(generateNote.pending, (state) => {
        state.generateStatus = 'loading';
        state.generateError = null;
      })
      .addCase(generateNote.fulfilled, (state, action) => {
        state.generateStatus = 'success';
        state.generatedNote = action.payload.note_sections;
        // Store the final prompts for debugging
        state.finalUserMessage = action.payload.user_message || '';
        state.finalSystemMessage = action.payload.system_message || '';
      })
      .addCase(generateNote.rejected, (state, action) => {
        state.generateStatus = 'error';
        state.generateError = action.payload;
      });

    // Convert formats
    builder
      .addCase(convertNoteFormats.pending, (state) => {
        state.convertStatus = 'loading';
        state.convertError = null;
      })
      .addCase(convertNoteFormats.fulfilled, (state, action) => {
        state.convertStatus = 'success';
        state.generatedFormats = {
          html: action.payload.html || state.generatedFormats.html,
          plain_text: action.payload.plain_text || state.generatedFormats.plain_text,
          rtf: action.payload.rtf || state.generatedFormats.rtf,
          formatted_text: action.payload.formatted_text || state.generatedFormats.formatted_text
        };
      })
      .addCase(convertNoteFormats.rejected, (state, action) => {
        state.convertStatus = 'error';
        state.convertError = action.payload;
      });

    // Fetch available models
    builder
      .addCase(fetchAvailableModels.pending, (state) => {
        state.modelsStatus = 'loading';
        state.modelsError = null;
      })
      .addCase(fetchAvailableModels.fulfilled, (state, action) => {
        state.modelsStatus = 'success';
        state.availableModels = action.payload.models || [];
      })
      .addCase(fetchAvailableModels.rejected, (state, action) => {
        state.modelsStatus = 'error';
        state.modelsError = action.payload;
      });

    // Fetch LLM prompt templates
    builder
      .addCase(fetchLLMPromptTemplates.pending, (state) => {
        state.llmPromptTemplatesStatus = 'loading';
        state.llmPromptTemplatesError = null;
      })
      .addCase(fetchLLMPromptTemplates.fulfilled, (state, action) => {
        state.llmPromptTemplatesStatus = 'success';
        state.availableLLMPromptTemplates = action.payload || [];
      })
      .addCase(fetchLLMPromptTemplates.rejected, (state, action) => {
        state.llmPromptTemplatesStatus = 'error';
        state.llmPromptTemplatesError = action.payload;
      });
  }
});

// Export actions
export const {
  selectCase,
  clearCase,
  setSelectedProvider,
  setSelectedTemplate,
  updateCustomNoteTemplateContent,
  setSelectedLLMPromptTemplate,
  updateCustomLLMPrompt,
  resetCustomLLMPrompt,
  updateProviderPreferences,
  resetProviderPreferences,
  updatePriorNotesConfig,
  addPriorNoteSection,
  removePriorNoteSection,
  clearPriorNotes,
  setEditedTranscript,
  resetTranscript,
  toggleTranscriptEditor,
  setCustomInstructions,
  updateGenerationOptions,
  toggleAdvancedOptions,
  setCompareWithNote,
  setComparisonView,
  setActiveTab,
  resetSandbox
} = noteGenerationSandboxSlice.actions;

// Selectors
export const selectAvailableCases = (state) => state.noteGenerationSandbox.availableCases;
export const selectSelectedCase = (state) => state.noteGenerationSandbox.selectedCase;
export const selectTranscript = (state) => state.noteGenerationSandbox.editedTranscript || state.noteGenerationSandbox.transcript;
export const selectTranscriptEdited = (state) => state.noteGenerationSandbox.transcriptEdited;
export const selectExistingNotes = (state) => state.noteGenerationSandbox.existingNotes;
export const selectAvailableProviders = (state) => state.noteGenerationSandbox.availableProviders;
export const selectAvailableTemplates = (state) => state.noteGenerationSandbox.availableTemplates;
export const selectAvailableModels = (state) => state.noteGenerationSandbox.availableModels;
export const selectAvailableLLMPromptTemplates = (state) => state.noteGenerationSandbox.availableLLMPromptTemplates;
export const selectModelsStatus = (state) => state.noteGenerationSandbox.modelsStatus;
export const selectLLMPromptTemplatesStatus = (state) => state.noteGenerationSandbox.llmPromptTemplatesStatus;
export const selectSelectedProviderId = (state) => state.noteGenerationSandbox.selectedProviderId;
export const selectSelectedTemplateId = (state) => state.noteGenerationSandbox.selectedTemplateId;
export const selectCustomNoteTemplateContent = (state) => state.noteGenerationSandbox.customNoteTemplateContent;
export const selectCustomInstructions = (state) => state.noteGenerationSandbox.customInstructions ?? '';
export const selectSelectedLLMPromptTemplateId = (state) => state.noteGenerationSandbox.selectedLLMPromptTemplateId;
export const selectCustomLLMPrompt = (state) => state.noteGenerationSandbox.customLLMPrompt;
export const selectProviderPreferencesOverride = (state) => state.noteGenerationSandbox.providerPreferencesOverride;
export const selectPriorNotesConfig = (state) => state.noteGenerationSandbox.priorNotesConfig;
export const selectGeneratedNote = (state) => state.noteGenerationSandbox.generatedNote;
export const selectGeneratedFormats = (state) => state.noteGenerationSandbox.generatedFormats;
export const selectGenerationOptions = (state) => state.noteGenerationSandbox.generationOptions;
export const selectGenerateStatus = (state) => state.noteGenerationSandbox.generateStatus;
export const selectGenerateError = (state) => state.noteGenerationSandbox.generateError;
export const selectConvertStatus = (state) => state.noteGenerationSandbox.convertStatus;
export const selectConvertError = (state) => state.noteGenerationSandbox.convertError;
export const selectFinalUserMessage = (state) => state.noteGenerationSandbox.finalUserMessage;
export const selectFinalSystemMessage = (state) => state.noteGenerationSandbox.finalSystemMessage;

export default noteGenerationSandboxSlice.reducer;
